import React, { FC } from 'react'
import styled from 'styled-components'

import { Link, showDropdownStyles } from '@sketch/components'

import Artboard from '../Artboard'
import PrototypeCardDropdown from '../PrototypeCardDropdown'

import { ReactComponent as PlayIcon } from '@sketch/icons/play-prism-32'
import { useFlag } from '@sketch/modules-common'
import { LocationDescriptorObject } from 'history'
import { useVersioning } from 'modules/versioning'

type PlayProps = {
  size: number
}

const Play = styled(PlayIcon)<PlayProps>`
  opacity: 0;
  position: absolute;
  margin: auto;

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;

  box-sizing: content-box;

  transition: opacity 200ms linear;
`

const Prototype = styled(Artboard)`
  ${showDropdownStyles};

  :hover,
  :focus,
  :focus-within {
    ${Play} {
      opacity: 1;
    }
  }
`

export interface PrototypeCardProps {
  prototypeId: string
  title: string
  src: string
  showTitle?: boolean
  showDropdown?: boolean
  iconSize?: 32 | 48
}

const PrototypeCard: FC<PrototypeCardProps> = ({
  prototypeId,
  title,
  src,
  showTitle,
  showDropdown = true,
  iconSize = 48,
}) => {
  const { getPathname, share } = useVersioning()
  const isBetterPrototypeNavBarOn = useFlag('better-prototype-nav-bar')
  const isWebGLPrototypePlayerOn = useFlag('webgl-prototype-player')
  const shouldAttachPrototypeIdToUrl =
    !isWebGLPrototypePlayerOn && isBetterPrototypeNavBarOn

  const pathname: string = isWebGLPrototypePlayerOn
    ? getPathname({
        routeKey: 'PROTOTYPE_VIEW_NEXT',
        routeParams: {
          shareID: share.identifier,
          prototypeArtboardUUID: prototypeId,
          currentArtboardUUID: prototypeId,
        },
      })
    : getPathname({
        routeKey: 'PROTOTYPE_VIEW',
        routeParams: {
          shareID: share.identifier,
          artboardUUID: prototypeId,
        },
      })

  const to: LocationDescriptorObject = {
    pathname,
    ...(shouldAttachPrototypeIdToUrl && {
      search: `?prototype=${prototypeId}`,
    }),
  }

  return (
    <Link data-testid="prototype-card-link" to={to}>
      <Prototype
        title={title}
        imageSrc={src}
        showTitle={showTitle}
        overlay={
          <>
            {showDropdown && (
              <PrototypeCardDropdown
                pathname={pathname}
                prototypeId={prototypeId}
              />
            )}
            <Play size={iconSize} />
          </>
        }
      />
    </Link>
  )
}

export default PrototypeCard
